import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Modal,
    Backdrop,
    Fade,
    List,
    ListItem,
    ListItemText,
    IconButton,
    OutlinedInput,
    Divider,
    useTheme,
    alpha,
    Grid,
    Alert,
} from "@mui/material";
import { Add, Delete, PhotoCamera, Close } from "@mui/icons-material";
import useAccountInfo from "../../hooks/useAccountInfo";
import { useSelector, useDispatch } from "react-redux";
import { updateUserInfo } from "../../store/userSlice";
import countries from "../../assets/json/countries.json";
import SingleFileUpload from "../fileUpload/FileUpload";


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const ProfileForm = () => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const { accountInfo, loading, error } = useAccountInfo();

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [birthDate, setBirthDate] = useState("");
    const [phone, setPhone] = useState("");
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [gender, setGender] = useState("");
    const [nationality, setNationality] = useState("TR");
    const [socialMediaList, setSocialMediaList] = useState([]);
    
    const [modalOpen, setModalOpen] = useState(false);
    const [socialMediaUrl, setSocialMediaUrl] = useState("");
    const [followerCount, setFollowerCount] = useState("");
    const [isUserApproved, setIsUserApproved] = useState(false);

    // API'den gelen verileri state'e bağlama
    useEffect(() => {
        if (accountInfo) {
            setFirstName(accountInfo.firstName || "");
            setLastName(accountInfo.lastName || "");
            setBirthDate(accountInfo.birthDate?.split("T")[0] || ""); // Tarih formatı düzenleniyor
            setPhone(accountInfo.phone || "");
            setNationality(accountInfo.countryCode || "TR"); // Set default to "Türkiye"
            setGender(accountInfo.gender || ""); // gender, response'da yoksa manuel eklenmeli
            setIsUserApproved(accountInfo.userApproved || false);
        }
    }, [accountInfo]);


    const handleSave = () => {
        const updatedInfo = {
            firstName,
            lastName,
            birthDate,
            phone,
            gender,
            countryCode: nationality,
        };
        dispatch(updateUserInfo(updatedInfo));
    };



    const handlePhotoUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfilePhoto(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePhoto = () => {
        setProfilePhoto(null);
    };

    const handleOpenModal = () => setModalOpen(true);

    const handleCloseModal = () => {
        setSocialMediaUrl("");
        setFollowerCount("");
        setModalOpen(false);
    };

    const handleAddSocialMedia = () => {
        if (socialMediaUrl && followerCount) {
            setSocialMediaList((prevList) => [
                ...prevList,
                { url: socialMediaUrl, followers: followerCount },
            ]);
            handleCloseModal();
        }
    };

    const handleRemoveSocialMedia = (index) => {
        setSocialMediaList((prevList) => prevList.filter((_, i) => i !== index));
    };



    if (loading) return <Typography>Yükleniyor...</Typography>;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <Box sx={{
            margin: "0 auto",
            maxWidth: 800,
            p: 3,
            borderRadius: 2,
            bgColor: 'transparent',
            backgroundColor: 'transparent',
            boxShadow: theme.customShadows.card,
            border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
        }}>
            {/* Form başlığı */}
            <Typography
                variant="h5"
                sx={{
                    color: theme.palette.mode === 'light'
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    mb: 3,
                    fontWeight: 600
                }}
            >
                Kişisel Bilgiler
            </Typography>


            <Alert
                severity="info"
                sx={{ mb: 3 }}
            >
                E-Devlet doğrulaması yapıldıktan sonra Ad Soyad bilgileri değiştirilemez
            </Alert>

            {/* TextField'lar için ortak stil */}
            <Box sx={{
                '& .MuiTextField-root, & .MuiFormControl-root': {
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        bgcolor: 'transparent',
                        '& fieldset': {
                            borderColor: theme.palette.mode === 'light'
                                ? alpha(theme.palette.grey[500], 0.32)
                                : alpha(theme.palette.grey[500], 0.2),
                        },
                        '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: theme.palette.primary.main,
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: theme.palette.mode === 'light'
                            ? theme.palette.grey[600]
                            : theme.palette.grey[400],
                    },
                    '& .MuiInputBase-input': {
                        color: theme.palette.mode === 'light'
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                    },
                    '& .MuiSelect-icon': {
                        color: theme.palette.mode === 'light'
                            ? theme.palette.grey[600]
                            : theme.palette.grey[400],
                    },
                },
            }}>
                <Grid container spacing={2}>
                    {/* Ad ve Soyad */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Ad"
                            fullWidth
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            disabled={isUserApproved}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Soyad"
                            fullWidth
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            disabled={isUserApproved}
                        />
                    </Grid>

                    {/* Telefon ve Doğum Tarihi */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Telefon"
                            fullWidth
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={isUserApproved}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Doğum Tarihi"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={birthDate}
                            onChange={(e) => setBirthDate(e.target.value)}
                            disabled={isUserApproved}
                        />
                    </Grid>

                    {/* Cinsiyet ve Uyruk */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="gender-select-label">Cinsiyet</InputLabel>
                            <Select
                                labelId="gender-select-label"
                                value={gender}
                                onChange={(e) => setGender(e.target.value)}
                                sx={{
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                <MenuItem value="">Seçiniz</MenuItem>
                                <MenuItem value="kadın">Kadın</MenuItem>
                                <MenuItem value="erkek">Erkek</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="nationality-select-label">Uyruk</InputLabel>
                            <Select
                                labelId="nationality-select-label"
                                value={nationality}
                                onChange={(e) => setNationality(e.target.value)}
                                sx={{
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                <MenuItem value="">Seçiniz</MenuItem>
                                {countries.map((country) => (
                                    <MenuItem key={country.countryCode} value={country.countryCode}>
                                        {country.country}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            {/* Profil Fotoğrafı Bölümü */}
            <Box sx={{
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "row",
                gap: 2,
                mb: 4,
            }}>
                {profilePhoto ? (
                    <Box sx={{
                        position: "relative",
                        width: 120,
                        height: 120,
                        borderRadius: "20%",
                        overflow: "hidden",
                        border: `2px solid ${theme.palette.divider}`,
                    }}>
                        <img
                            src={profilePhoto}
                            alt="Profile"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                        <IconButton
                            onClick={handleRemovePhoto}
                            sx={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                                }
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                ) : (
                    <div>
                         <Typography variant="h8" gutterBottom sx={{ marginTop: 2 }}>Profil Fotoğrafınız</Typography>
                         <SingleFileUpload />
                    </div>
                )}
            </Box>

            {/* Kaydet Butonu */}
            <Box sx={{ mt: 4, textAlign: "right" }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                        },
                    }}
                >
                    Kaydet
                </Button>
            </Box>

            <Divider sx={{
                my: 4,
                borderColor: theme.palette.divider
            }} />

            {/* Sosyal Medya Bölümü
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
            }}>
                <Typography 
                    variant="h6"
                    sx={{ 
                        color: theme.palette.text.primary,
                        fontWeight: 600 
                    }}
                >
                    Sosyal Medya Hesapları
                </Typography>
                <Button 
                    variant="outlined" 
                    startIcon={<Add />} 
                    onClick={handleOpenModal}
                    sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                    }}
                >
                    Yeni Ekle
                </Button>
            </Box> 
            */}

            {/* Sosyal Medya Listesi 
            {socialMediaList.length > 0 ? (
                <List>
                    {socialMediaList.map((account, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                bgcolor: theme.palette.mode === 'light'
                                    ? alpha(theme.palette.grey[100], 0.5)
                                    : alpha(theme.palette.grey[800], 0.5),
                                borderRadius: 1,
                                mb: 1,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    bgcolor: theme.palette.mode === 'light'
                                        ? alpha(theme.palette.grey[200], 0.7)
                                        : alpha(theme.palette.grey[700], 0.7),
                                },
                            }}
                        >
                            <ListItemText
                                primary={account.url}
                                secondary={`Takipçi Sayısı: ${account.followers}`}
                                primaryTypographyProps={{
                                    color: theme.palette.text.primary,
                                }}
                                secondaryTypographyProps={{
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <IconButton
                                onClick={() => handleRemoveSocialMedia(index)}
                                sx={{
                                    color: theme.palette.error.main,
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.error.main, 0.08),
                                    },
                                }}
                            >
                                <Delete />
                            </IconButton>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Typography 
                    color="textSecondary"
                    sx={{ 
                        color: theme.palette.text.secondary,
                        fontStyle: 'italic'
                    }}
                >
                    Henüz sosyal medya hesabı eklenmedi.
                </Typography>
            )}
                */}

            {/* Modal */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={modalOpen}>
                    <Box sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: theme.palette.mode === 'light'
                            ? theme.palette.background.paper
                            : theme.palette.background.default,
                        boxShadow: theme.customShadows.dialog,
                        borderRadius: 2,
                        p: 4,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                    }}>
                        <Typography variant="h6" gutterBottom>
                            Sosyal Medya Hesabı Ekle
                        </Typography>
                        <TextField
                            label="Sosyal Medya URL"
                            fullWidth
                            sx={{ marginBottom: 2 }}
                            value={socialMediaUrl}
                            onChange={(e) => setSocialMediaUrl(e.target.value)}
                        />
                        <TextField
                            label="Takipçi Sayısı"
                            type="number"
                            fullWidth
                            sx={{ marginBottom: 2 }}
                            value={followerCount}
                            onChange={(e) => setFollowerCount(e.target.value)}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                            <Button onClick={handleCloseModal} variant="outlined">
                                İptal
                            </Button>
                            <Button onClick={handleAddSocialMedia} variant="contained">
                                Ekle
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default ProfileForm;

